import axiosForExternalCall from 'axios';
import dayjs from 'dayjs';
import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import pick from 'lodash/pick';
import startCase from 'lodash/startCase';
import { defineStore } from 'pinia';
import { nextTick, ref } from 'vue';

import { LibraryAPI } from '@/apis';
import { externalAxios } from '@/apis/external-apis';
import { axios as instagramAxios } from '@/apis/instagram';
import { axios } from '@/apis/library';
import * as tiktokApi from '@/apis/tiktok';
import * as youtubeApi from '@/apis/youtube';
import enumTypes, { linkedinMediaTypes } from '@/app/library/constants';
import { DEMO_VIDEO_SOURCE_ID, DEMO_YOUTUBE_VIDEO_URL } from '@/app/youtube/constants';
import { constants, mediaSourceType } from '@/config';
import { useAuthStore } from '@/stores/auth';
import { useFlagStore } from '@/stores/flag';
import { localizeCurrency } from '@/utils/formatters';
import { stringifyQuery } from '@/utils/query';

export const formatYouTubeAudienceObject = (sourceData) => {
  const directOrUnknownTrafficSourceTypes = ['UNKNOWNMOBILEORDIRECT', 'NOLINKOTHER'];
  // ignoring these attributes as they describe values relating to external sources,
  // and we use the data within external_traffic_sources to display
  // external source information.
  const propertiesToIgnore = ['EXTURL', 'NOLINKEMBEDDED'];
  const {
    externalTrafficSources,
    trafficSourceTotals,
    topSearchTerms,
    countryDemographics,
    ageGenderDemographics,
  } = sourceData;

  // Recreate original formatting of certain metrics
  const formattedCountryDemographics = {};
  Object.entries(countryDemographics || {}).forEach(([countryCode, value]) => {
    formattedCountryDemographics[countryCode.toUpperCase()] = value;
  });
  const formattedTrafficSourceTotals = {};
  Object.entries(trafficSourceTotals || {}).forEach(([trafficSource, value]) => {
    formattedTrafficSourceTotals[trafficSource.toUpperCase()] = value;
  });

  let audience = {
    externalTrafficSources,
    topSearchTerms,
    countryDemographics: formattedCountryDemographics,
  };

  if (formattedTrafficSourceTotals) {
    const withinYouTube = {};
    const directOrUnknown = {};

    Object.keys(formattedTrafficSourceTotals).forEach((key) => {
      if (propertiesToIgnore.includes(key)) return;

      if (directOrUnknownTrafficSourceTypes.includes(key)) {
        directOrUnknown[key] = formattedTrafficSourceTotals[key];
      } else {
        withinYouTube[key] = formattedTrafficSourceTotals[key];
      }
    });

    audience = {
      ...audience,
      withinYouTube,
      directOrUnknown,
    };
  }

  if (ageGenderDemographics) {
    const formattedStats = {};
    Object.keys(ageGenderDemographics).forEach((key) => {
      // keys are of the form: age<lowerBound>-<upperBound>_<gender> ex: age13-17_male
      const [ageGroup, gender] = key.split('_');

      if (!formattedStats[ageGroup]) {
        formattedStats[ageGroup] = {};
      }

      formattedStats[ageGroup][gender] = ageGenderDemographics[key];
    });
    audience = {
      ...audience,
      ageGenderDemographics: formattedStats,
    };
  }
  return audience;
};

function parseUser(sourceData) {
  let userName = null;
  if (sourceData.instagramUser) {
    userName = sourceData.instagramUser.handle;
  }
  return { userName };
}

export const formatYouTubeMediaDetail = (isOwned, sourceData) => {
  const flagStore = useFlagStore();
  const youtubeBaseUrl = 'https://www.youtube.com';

  // Demo brands will have videos with sourceVideoIds like "test_video_id_<brand_id>_<random_string>"
  // we want to show a demo video for these videos
  let { youtubeLink: postUrl, sourceVideoId } = sourceData;
  if (sourceVideoId?.startsWith('test_video_')) {
    sourceVideoId = DEMO_VIDEO_SOURCE_ID;
    postUrl = DEMO_YOUTUBE_VIDEO_URL;
  }

  const youtubeMediaDetail = {
    userName: sourceData.accountDetails?.title,
    avatarUrl: sourceData.accountDetails?.thumbnailUrl,
    postTitle: sourceData.title,
    postMessage: sourceData.description,
    datePosted: sourceData.sourcePublishedAt,
    platformHomePageUrl: `${youtubeBaseUrl}/channel/${sourceData.accountDetails?.sourceChannelId}`,
    sourceVideoId,
    postUrl,
  };

  if (isOwned) {
    youtubeMediaDetail.insights = {
      videoViews: sourceData.views,
      avgViewDuration: sourceData.avgViewDuration,
      avgViewPercentage: sourceData.avgViewPercentage,
      watchTime: sourceData.estSecondsWatched,
      subscribers: sourceData.subscribers,
      ...(flagStore.flags?.youtubeCompliance ? { totalEngagements: sourceData.engagements } : {}),
      likes: sourceData.likes,
      dislikes: sourceData.dislikes,
      comments: sourceData.comments,
      shares: sourceData.shares,
      addedToPlaylist: sourceData.videosAddedToPlaylist,
      cardClicks: sourceData.cardClicks,
      cardClickRate: sourceData.cardClickRate,
      cardImpressions: sourceData.cardImpressions,
      cardTeaserClicks: sourceData.cardTeaserClicks,
      cardTeaserClickRate: sourceData.cardTeaserClickRate,
      cardTeaserImpressions: sourceData.cardTeaserImpressions,
      premiumViews: sourceData.premiumViews,
      premiumWatchTime: sourceData.estPremiumSecondsWatched,
    };
    youtubeMediaDetail.audience = formatYouTubeAudienceObject(sourceData);
  } else {
    youtubeMediaDetail.insights = {
      videoViews: sourceData.views,
      likes: sourceData.likes,
      comments: sourceData.comments,
    };
  }

  return youtubeMediaDetail;
};

function positiveNumber(value) {
  return value > 0 ? value : 0;
}

function adAccountScopedInsightsMapper(adAccountScopedInsights) {
  return adAccountScopedInsights.map((scopedInsights) => ({
    adAccountId: scopedInsights.ad_account_id ?? scopedInsights.adAccountId,
    reach: positiveNumber(scopedInsights.reach),
    frequency: positiveNumber(scopedInsights.frequency),
  }));
}

export function formatInstagramData(data) {
  let { boosted_data: rawBoostedData } = data;
  rawBoostedData = rawBoostedData ?? {};
  const likeCountPaid = positiveNumber(rawBoostedData.like_count_paid);
  const commentsCountPaid = positiveNumber(rawBoostedData.comments_count_paid);
  const impressionsPaid = positiveNumber(rawBoostedData.impressions_paid);
  const videoViewsPaid = positiveNumber(rawBoostedData.video_views);
  const amountSpentPaid = positiveNumber(rawBoostedData.amount_spent);
  const clicksPaid = positiveNumber(rawBoostedData.clicks);
  const costPerThruplayPaid = positiveNumber(rawBoostedData.cost_per_thruplay);
  const cpcPaid = positiveNumber(rawBoostedData.cpc);
  const cpmPaid = positiveNumber(rawBoostedData.cpm);
  const ctrPaid = positiveNumber(rawBoostedData.ctr);
  const reachPaid = positiveNumber(rawBoostedData.reach);
  const reactionsPaid = positiveNumber(rawBoostedData.reactions);
  const sharesPaid = positiveNumber(rawBoostedData.shares);
  const savedPaid = positiveNumber(rawBoostedData.saved);
  const thruplaysPaid = positiveNumber(rawBoostedData.thruplays);
  const videoPlays25Paid = positiveNumber(rawBoostedData.video_plays_25);
  const videoPlays50Paid = positiveNumber(rawBoostedData.video_plays_50);
  const videoPlays75Paid = positiveNumber(rawBoostedData.video_plays_75);
  const videoPlays100Paid = positiveNumber(rawBoostedData.video_plays_100);
  const totalEngagements = positiveNumber(data.sum_total_engagement);
  const totalEngagementsOrganic = positiveNumber(data.total_engagement);
  const totalEngagementsPaid = positiveNumber(data.paid_total_engagement);
  const engagementRatePaid = positiveNumber(data.paid_total_engagement_rate);
  const frequencyPaid = positiveNumber(rawBoostedData.frequency ?? impressionsPaid / reachPaid);
  const shares = positiveNumber(data.shares);
  const saved = positiveNumber(data.saved);
  const adAccountScopedInsights = adAccountScopedInsightsMapper(
    rawBoostedData.ad_account_scoped_insights ?? [],
  );
  const { profile_activity: profileActivity } = data;
  // Empty profileActivity means unavailable but some older posts created before this metric was introduced
  // will also have it empty even though it is supported
  const defaultProfileActivityValue = Object.keys(profileActivity).length ? 0 : null;
  const boostedData = {};
  if (Object.keys(rawBoostedData ?? {}).length > 0) {
    Object.assign(boostedData, {
      totalEngagementsOrganic,
      totalEngagementsPaid,
      sharesTotal: shares + sharesPaid,
      savedTotal: saved + savedPaid,
      videoViewsOrganic: data.video_views,
      reachTotal: (data.reach ?? 0) + reachPaid,
      likesOrganic: data.like_count,
      commentsOrganic: data.comments_count,
      impressionsOrganic: data.impressions,
      amountSpentPaid,
      clicksPaid,
      likeCountPaid,
      commentsCountPaid,
      impressionsPaid,
      videoViewsPaid,
      costPerThruplayPaid,
      cpcPaid,
      cpmPaid,
      ctrPaid,
      engagementRatePaid,
      reachPaid,
      reactionsPaid,
      sharesPaid,
      savedPaid,
      thruplaysPaid,
      videoPlays25Paid,
      videoPlays50Paid,
      videoPlays75Paid,
      videoPlays100Paid,
      videoPlaysTotalPaid:
        videoPlays25Paid + videoPlays50Paid + videoPlays75Paid + videoPlays100Paid,
      frequencyPaid,
      adAccountScopedInsights,
    });
  }
  const relationshipData = {};
  if ((data.is_mentioned || data.is_tagged) && data?.relationship_post) {
    relationshipData.followersGained = data.relationship_post.followers_gained;
  }
  const emvData = data.is_mentioned || data.is_tagged ? { emv: data.emv } : {};
  return {
    effectivenessEngagements: data.effectiveness_engagements,
    effectiveness: data.effectiveness,
    engagementRate: data.engagement,
    engagementRateImpressions: data.engagement_rate_impressions,
    engagementRateViews: data.engagement_rate_views,
    impressions: impressionsPaid + data.impressions,
    reach: data.reach,
    totalEngagements,
    likes: likeCountPaid + data.like_count,
    comments: commentsCountPaid + data.comments_count,
    is_commented: data.is_commented,
    saved: data.saved,
    shares,
    follows: data.follows,
    profileVisits: data.profile_visits,
    profileClicks: profileActivity.total ?? defaultProfileActivityValue,
    bioLinkClicks: profileActivity.bio_link_clicked ?? defaultProfileActivityValue,
    calls: profileActivity.call ?? defaultProfileActivityValue,
    directions: profileActivity.direction ?? defaultProfileActivityValue,
    emails: profileActivity.email ?? defaultProfileActivityValue,
    other: profileActivity.other ?? defaultProfileActivityValue,
    text: profileActivity.text ?? defaultProfileActivityValue,
    videoViews: data.total_video_views,
    views: data.views,
    showBusinessInsights: data.show_business_insights,
    ...boostedData,
    ...relationshipData,
    ...emvData,
  };
}

function formatInstagramMediaDetail(data) {
  const { boostedData: rawBoostedData } = data.sourceData;
  const boostedData = {};
  if (Object.keys(rawBoostedData ?? {}).length > 0) {
    boostedData.adAccountScopedInsights = adAccountScopedInsightsMapper(
      rawBoostedData.adAccountScopedInsights ?? [],
    );
    boostedData.commentsOrganic = positiveNumber(data.sourceData.commentsCount);
    boostedData.commentsCountPaid = positiveNumber(rawBoostedData.commentsCountPaid);
    boostedData.comments = positiveNumber(rawBoostedData.commentsTotal);
    boostedData.impressionsPaid = positiveNumber(rawBoostedData.impressionsPaid);
    boostedData.impressions = positiveNumber(data.sourceData.totalImpressions);
    boostedData.impressionsOrganic = positiveNumber(data.sourceData.impressions);
    boostedData.videoViewsOrganic = positiveNumber(data.sourceData.videoViews);
    boostedData.videoViewsPaid = positiveNumber(rawBoostedData.videoViews);
    boostedData.videoViewsTotal = positiveNumber(data.sourceData.totalVideoViews);
    boostedData.amountSpentPaid = positiveNumber(rawBoostedData.amountSpent);
    boostedData.clicksPaid = positiveNumber(rawBoostedData.clicks);
    boostedData.costPerThruplayPaid = positiveNumber(rawBoostedData.costPerThruplay);
    boostedData.cpcPaid = positiveNumber(rawBoostedData.cpc);
    boostedData.cpmPaid = positiveNumber(rawBoostedData.cpm);
    boostedData.ctrPaid = positiveNumber(rawBoostedData.ctr);
    boostedData.totalEngagements = positiveNumber(data.sourceData.sumTotalEngagement);
    boostedData.totalEngagementsOrganic = positiveNumber(data.sourceData.totalEngagement);
    boostedData.totalEngagementsPaid = positiveNumber(data.sourceData.paidTotalEngagement);
    boostedData.paidTotalEngagements = positiveNumber(data.sourceData.paidTotalEngagement);
    boostedData.engagementRatePaid = positiveNumber(data.sourceData.paidTotalEngagementRate);
    boostedData.engagementRate = positiveNumber(data.sourceData.engagement);
    boostedData.engagementRateImpressions = positiveNumber(
      data.sourceData.engagementRateImpressions,
    );
    boostedData.reachPaid = positiveNumber(rawBoostedData.reach);
    boostedData.reachTotal = positiveNumber(data.sourceData.totalReach);
    boostedData.reactionsPaid = positiveNumber(rawBoostedData.reactions);
    boostedData.sharesTotal = positiveNumber(data.sourceData.totalShares);
    boostedData.sharesOrganic = positiveNumber(data.sourceData.shares);
    boostedData.sharesPaid = positiveNumber(rawBoostedData.shares);
    boostedData.thruplaysPaid = positiveNumber(rawBoostedData.thruplays);
    boostedData.videoPlays25Paid = positiveNumber(rawBoostedData.videoPlays25);
    boostedData.videoPlays50Paid = positiveNumber(rawBoostedData.videoPlays50);
    boostedData.videoPlays75Paid = positiveNumber(rawBoostedData.videoPlays75);
    boostedData.videoPlays100Paid = positiveNumber(rawBoostedData.videoPlays100);
    boostedData.reach = positiveNumber(data.sourceData.reach);
    boostedData.effectiveness = positiveNumber(data.sourceData.effectiveness);
    boostedData.likes = positiveNumber(rawBoostedData.likesTotal);
    boostedData.likeCountPaid = positiveNumber(rawBoostedData.likeCountPaid);
    boostedData.likesOrganic = positiveNumber(data.sourceData.likeCount);
    boostedData.savedTotal = positiveNumber(data.sourceData.totalSaved);
    boostedData.saved = positiveNumber(data.sourceData.saved);
    boostedData.savedPaid = positiveNumber(rawBoostedData.saved);
    boostedData.frequencyPaid = positiveNumber(rawBoostedData.frequency);
    boostedData.startTimeForAd = rawBoostedData.startTimeForAd;
    boostedData.replays = positiveNumber(data.sourceData.replays);
    boostedData.totalPlays = positiveNumber(data.sourceData.totalPlays);
    boostedData.avgTimeViewedSec = positiveNumber(data.sourceData.avgTimeViewedSec);
    boostedData.totalTimeViewedSec = positiveNumber(data.sourceData.totalTimeViewedSec);
  }
  const contentRightsRequested = data.sourceData.rightsRequestMedia !== null;
  const contentRightsRequestType = data.sourceData.rightsRequestMedia
    ? data.sourceData.rightsRequestMedia.type
    : null;
  return {
    userName: data.sourceData ? parseUser(data.sourceData).userName : null,
    datePosted: data.sourceData.timestamp,
    postUrl: data.sourceData.url,
    thumbnailUrl: data.sourceData.media?.[0]?.urls?.ratio,
    insights: null,
    likeshopClicks: data.likeshopClicks,
    // special fields
    postCaption: data.sourceData.caption,
    atMentions: data.sourceData.photoTags,
    isRiq: data.sourceData.isRiq,
    relationship: data.sourceData.relationship,
    contentRightsRequested,
    contentRightsRequestType,
    rightsRequestMedia: data.sourceData.rightsRequestMedia,
    type: data.sourceData.type,
    isReel: data.sourceData.type?.toLowerCase() === 'reel',
    hasCopyrightIssue: data.sourceData.hasCopyrightIssue,
    isBusinessAccount: data.sourceData.instagramUser?.isBusiness,
    // carousel field
    media: data.sourceData?.media?.map((m) => ({ ...m, postSourceId: data.sourceId })),
    boostedData,
  };
}

function formatPinterestMediaDetail(data, mediaDetail) {
  let insightList;
  let allInsights = {};
  const pin = data.sourceData;
  const carouselUrls = pin.additionalMediaUrls;
  let media;
  if (carouselUrls) {
    const carouselMedia = carouselUrls.map((mediaObject) => ({
      originalUrl: mediaObject.url,
      urls: {
        full: mediaObject.url,
        ratio: mediaObject.url,
        thumbs: mediaObject.url,
      },
      mediaType: mediaObject.mediaType.toUpperCase(),

      // show badge for pinterest carousel videos
      sourceType: enumTypes.PINTEREST_OWNED,
      postSourceId: data.sourceId,
      postUrl: pin.link,
    }));
    media = [mediaDetail, ...carouselMedia];
  }
  if (Object.keys(pin).length > 0) {
    allInsights.allTime = {};
    Object.keys(pin.insights).forEach((key) => {
      const interval = pin.insights[key];
      const isAllTime = key === 'allTime';

      const engagementRate = interval.engagement;
      const videoViews = isAllTime ? pin.totalVideoViews : interval.videoViews;
      const averageWatchTime = isAllTime ? pin.averageVideoWatchTime : interval.videoAverageTime;
      const linkClicks = isAllTime
        ? pin.totalClicks
        : interval.outboundClick || interval.clickthrough;
      const saves = isAllTime ? pin.totalSaves : interval.save;
      const impressions = isAllTime ? pin.totalImpressions : interval.impression;
      const closeups = isAllTime ? pin.totalCloseups : interval.pinClick || interval.closeup;
      const engagements = isAllTime ? pin.engagements : interval.engagements;
      const lastUpdated = interval.lastUpdated;

      insightList = {
        engagementRate,
        engagements,
        videoViews,
        averageWatchTime,
        linkClicks,
        saves,
        impressions,
        closeups,
        lastUpdated,
      };
      allInsights = { ...allInsights, ...{ [key]: insightList } };
    });
    allInsights.allTime.engagementRate = pin.engagement;
  }
  return {
    userName: pin.pinner ? pin.pinner.username : null,
    avatarUrl: pin.pinner?.avatarUrl || pin.pinner?.imageUrl,
    postUrl: pin.link,
    datePosted: pin.pinCreatedAt,
    insights: allInsights,
    // special fields
    linkToSite: pin.sourceUrl,
    title: pin.title,
    boardName: pin.pinBoardName,
    description: pin.note && pin.note.trim() !== '' ? pin.note : '-',
    pinterest_video_id: pin.pinterestVideoId,
    media,
    pinBoardPrivacy: pin.pinBoardPrivacy,
    video_status: pin.videoStatus,
    sourcePostType: pin.type,
  };
}

function formatUploadMediaDetail(data) {
  return {
    firstName: data.sourceData.firstName,
    lastName: data.sourceData.lastName,
    thumbnailUrl: data.urls.ratio,
    // make source data nested inside uploadSourceData instead of mixin at same level
    // in case data with same field name that come from different sources override each other.
    uploadSourceData: {
      ...data.sourceData,
      uploadTime: data.sourceCreatedAt,
    },
  };
}

function formatAiGenerationMediaDetail(data) {
  return {
    firstName: data.sourceData.firstName,
    lastName: data.sourceData.lastName,
    thumbnailUrl: data.urls.ratio,
    // make source data nested inside aiGenerationSourceData instead of mixin at same level
    // in case data with same field name that come from different sources override each other.
    aiGenerationSourceData: {
      ...data.sourceData,
      savedTime: data.sourceCreatedAt,
    },
  };
}

function formatInstagramStoryMediaDetail(data) {
  const instagramSourceData = {
    userName: data.sourceData ? parseUser(data.sourceData).userName : null,
    user: data.sourceData ? data.sourceData.instagramUser : null,
    datePosted: data.sourceData.date,
    postUrl: data.sourceData.url,
    importedViaDm: data.sourceData.importedViaDm,
    insights: {
      reach: data.sourceData.reach,
      impressions: data.sourceData.views,
      completionRate: data.sourceData.completionRate,
      completionRateViews: data.sourceData.completionRateViews,
      exits: data.sourceData.exits,
      exitRate: data.sourceData.exitRate,
      exitRateViews: data.sourceData.exitRateViews,
      tapsForward: data.sourceData.tapsForward,
      tapsBack: data.sourceData.tapsBack,
      replies: data.sourceData.replies,
      emv: data.sourceData.emv,
      emvViews: data.sourceData.emvViews,
      views: data.sourceData.viewsV2,
    },
  };
  if (data.sourceData.shortUrl) {
    if (typeof data.sourceData.shortUrl === 'string') {
      instagramSourceData.shortURLData = {
        swipeUpLink: data.sourceData.shortUrl,
      };
    } else {
      instagramSourceData.shortURLData = {
        swipeUps: data.sourceData.shortUrl.totalClicks,
        swipeUpLink: data.sourceData.shortUrl.url,
        trackableLink: data.meta.storyLink,
      };
    }
  }
  return instagramSourceData;
}

function formatFacebookAdsMediaDetail(data, brand, adId) {
  let adsSourceData = {
    userName: null,
    adsManagerUrl: null,
    datePosted: data.source_created_at,
    caption: '',
    // Ids of all media in the same ad.
    mediaIds: [],
    adEffectiveStatus: 'INACTIVE',
    title: null,
    link: null,
  };
  let adsInfo = {};
  let insights = {};
  const publishersInsights = {};
  if (data.sourceData.length > 0) {
    let firstAd = {};
    if (adId) {
      if (data.sourceData.length === 1) {
        [firstAd] = data.sourceData;
      } else {
        firstAd = data.sourceData.filter((item) => item.fbAdId === adId.toString());
        [firstAd] = firstAd;
      }
    } else {
      firstAd = data.sourceData.reduce((prev, curr) => {
        const timeDiff = dayjs(curr.adSetStartTime).diff(dayjs(prev.adSetStartTime));
        if (timeDiff < 0) {
          return prev;
        }
        if (timeDiff === 0) {
          return (prev.reach || 0) < (curr.reach || 0) ? curr : prev;
        }
        return curr;
      });
    }
    let endDate;
    if (!firstAd.adSetEndTime) {
      endDate = firstAd.adSetEffectiveStatus === 'ACTIVE' ? 'In Progress' : '-';
    } else {
      endDate = dayjs(firstAd.adSetEndTime).format('MMM DD, YYYY');
    }

    adsInfo = {
      startDate: dayjs(firstAd.adSetStartTime).format('MMM DD, YYYY'),
      endDate,
      distributionChannels: firstAd.adSetPublisherPlatforms
        ? firstAd.adSetPublisherPlatforms.map((platform) => startCase(platform)).join(', ')
        : '-',
      adCreatedTime: dayjs(firstAd.adCreatedTime).format('MMM DD, YYYY'),
      adName: firstAd.name,
      adSet: firstAd.adSetName,
      campaignName: firstAd.campaignName,
      objective: firstAd.campaignObjective,
      audienceGenders: firstAd.audienceGenders,
      audienceLocations: firstAd.audienceLocations,
      audienceAgeMax: firstAd.audienceAgeMax,
      audienceAgeMin: firstAd.audienceAgeMin,
      adSetEndTime: firstAd.adSetEndTime,
      campaignStopTime: firstAd.campaignStopTime,
      id: firstAd.id,
      adIsDynamic: firstAd.creativeIsDynamic,
      adDynamicMediaCount: firstAd.creativeDynamicMediaCount,
      adIsBoosted: firstAd.isBoosted,
    };
    if (firstAd.publisherInsights) {
      Object.keys(firstAd.publisherInsights).forEach((key) => {
        let sourceInsights = {};
        const interval = firstAd.publisherInsights[key];
        sourceInsights = {
          roas: interval.purchaseRoas,
          reach: interval.reach,
          impressions: interval.impressions,
          ctr: interval.ctrLinkClicks,
          cpc: interval.cpcLinkClicks
            ? localizeCurrency(interval.cpcLinkClicks, firstAd.accountCurrency)
            : '',
          linkClicks: interval.linkClicks,
          spend: interval.spend,
        };
        if (firstAd.creative_type === 'VIDEO') {
          sourceInsights.videoViews = interval.videoViews ? interval.videoViews : 0;
        }
        publishersInsights[key] = sourceInsights;
      });
    }
    insights = {
      roas: firstAd.purchaseRoas,
      reach: firstAd.reach,
      impressions: firstAd.impressions,
      ctr: firstAd.ctrLinkClicks,
      cpc: localizeCurrency(firstAd.cpcLinkClicks, firstAd.accountCurrency),
      linkClicks: firstAd.linkClicks,
      spend: firstAd.spend,
    };
    if (firstAd.creative_type === 'VIDEO') {
      insights.videoViews = firstAd.videoViews ? firstAd.videoViews : 0;
    }

    adsSourceData = {
      ...adsSourceData,
      publishersInsights,
      userName: firstAd.campaignName,
      adsManagerUrl: firstAd.fbAdsManagerUrl,
      caption: firstAd.creativeBody,
      // brand media ids is formatted like ['<brand_id>:<media_id>', ....]
      mediaIds: firstAd.brandMediaIds
        .filter((bm) => Number(bm.split(':')[0]) === brand.id)
        .map((bm) => bm.split(':')[1]),
      adEffectiveStatus: firstAd.effectiveStatus,
      adSetEffectiveStatus: firstAd.adSetEffectiveStatus,
      campaignEffectiveStatus: firstAd.campaignEffectiveStatus,
      title: firstAd.creativeTitle,
      link: firstAd.creativeLink,
      dateRange:
        firstAd.insightsStartDate && firstAd.insightsEndDate
          ? [
              dayjs(firstAd.insightsStartDate).format('YYYY-MM-DD'),
              dayjs(firstAd.insightsEndDate).format('YYYY-MM-DD'),
            ]
          : null,
    };
  }
  return {
    ...adsSourceData,
    adsInfo,
    insights,
  };
}

function extractFacebookPromotedAndOrganicMetrics(sourceData) {
  return {
    promotedAmountSpent: sourceData?.amountSpend,
    // promotedClicks missing

    promotedAndOrganicComments: sourceData?.paidAndOrganicComments,
    organicComments: sourceData?.organicComments,
    promotedComments: sourceData?.paidComments,

    promotedCostPerThruPlays: sourceData?.costPerThruplays,
    promotedCpc: sourceData?.cpc,
    promotedCpm: sourceData?.cpm,
    promotedCtr: sourceData?.ctr,

    organicEffectiveness: sourceData?.organicEffectiveness,

    promotedAndOrganicEngagements: sourceData?.paidAndOrganicEngagements,
    organicEngagements: sourceData?.organicEngagements,
    promotedEngagements: sourceData?.paidEngagements,

    organicEngagementRate: sourceData?.organicEngagementRate,
    promotedEngagementRate: sourceData?.paidEngagementRate,

    promotedFrequency: sourceData?.frequency,

    promotedAndOrganicImpressions: sourceData?.paidAndOrganicImpressions,
    organicImpressions: sourceData?.organicImpressions,
    promotedImpressions: sourceData?.paidImpressions,

    promotedAndOrganicLinkClicks: sourceData?.paidAndOrganicLinkClicks,
    organicLinkClicks: sourceData?.organicLinkClicks,
    paidLinkClicks: sourceData?.paidLinkClicks,

    // promotedPageLikes missing

    promotedAndOrganicReach: sourceData?.paidAndOrganicReach,
    organicReach: sourceData?.organicReach,
    promotedReach: sourceData?.paidReach,

    promotedAndOrganicReactions: sourceData?.paidAndOrganicReactions,
    organicReactions: sourceData?.organicReactions,
    promotedReactions: sourceData?.paidReactions,

    promotedAndOrganicShares: sourceData?.paidAndOrganicShares,
    organicShares: sourceData?.organicShares,
    promotedShares: sourceData?.paidShares,

    promotedThruPlays: sourceData?.thruplays,

    promotedAndOrganicVideoCompleteViews: sourceData?.paidAndOrganicVideoCompleteViews,
    organicVideoCompleteViews: sourceData?.organicVideoCompleteViews,
    promotedVideoCompleteViews: sourceData?.paidVideoCompleteViews,

    promotedVideoPlays25: sourceData?.videoPlays25,
    promotedVideoPlays50: sourceData?.videoPlays50,
    promotedVideoPlays75: sourceData?.videoPlays75,

    promotedAndOrganicVideoViews: sourceData?.paidAndOrganicVideoViews,
    organicVideoViews: sourceData?.organicVideoViews,
    promotedVideoViews: sourceData?.paidVideoViews,

    organicViews: sourceData?.organicViews,
    promotedViews: sourceData?.paidViews,
    promotedAndOrganicViews: sourceData?.paidAndOrganicViews,

    organicEngagementRateV2: sourceData?.organicEngagementRateV2,
    promotedEngagementRateV2: sourceData?.paidEngagementRateV2,
    promotedAndOrganicEngagementRateV2: sourceData?.paidAndOrganicEngagementRateV2,

    organicEngagementsV2: sourceData?.organicEngagementsV2,
    promotedAndOrganicEngagementsV2: sourceData?.paidAndOrganicEngagementsV2,

    organicEffectivenessV2: sourceData?.organicEffectivenessV2,
    promotedEffectivenessV2: sourceData?.paidEffectivenessV2,
    promotedAndOrganicEffectivenessV2: sourceData?.paidAndOrganicEffectivenessV2,

    // no V2 for organic reach from backend, just use organicReach
    organicReachV2: sourceData?.organicReach,
    promotedReachV2: sourceData?.paidReachV2,
    promotedAndOrganicReachV2: sourceData?.paidAndOrganicReachV2,

    adAccountScopedInsights: adAccountScopedInsightsMapper(
      sourceData?.adAccountScopedInsights ?? [],
    ),
  };
}

function formatFacebookMediaDetail(data, brand, mediaDetail, adId) {
  if (mediaDetail.sourceType === 'FACEBOOK_ADS') {
    return formatFacebookAdsMediaDetail(data, brand, adId);
  }
  if (!enumTypes.FACEBOOK_SOURCE_LIST.includes(mediaDetail.sourceType)) {
    return {};
  }
  const isReel = data.sourceData.type?.toLowerCase() === 'reel';
  const isBoosted = data.sourceData.isBoosted;
  const isCompetitive = enumTypes.FACEBOOK_COMPETITIVE_SOURCE_LIST.includes(mediaDetail.sourceType);

  const reelInsights = isReel
    ? {
        plays: data.sourceData.reel.blueReelsPlayCount,
        replays: data.sourceData.reel.fbReelsReplayCount,
        totalPlays: data.sourceData.reel.fbReelsTotalPlays,
        uniqueImpressions: data.sourceData.reel.postImpressionsUnique,
        avgTimeWatched: data.sourceData.reel.postVideoAvgTimeWatched, // in milliseconds
        totalTimeWatched: data.sourceData.reel.postVideoViewTime, // in milliseconds
        follows: data.sourceData.reel.postVideoFollowers,
        percentageViewed: data.sourceData.reel.postVideoRetentionGraph,
        effectivenessReels: data.sourceData.reel.effectiveness,
      }
    : {};

  const metricNames = [];
  if (isCompetitive) {
    metricNames.push(
      'engagementRate',
      'totalComments',
      'totalEngagements',
      'totalLikes',
      'totalReactions',
      'totalShares',
    );
  } else {
    metricNames.push(
      // deprecated names:
      'reactions',
      'comments',
      'shares',
      'engagementRate',
      'totalEngagements',
      'effectiveness',
      'impressions',
      'reach',
      'linkClicks',
      'videoViews',
      'videoCompleteViews',
      'otherClicks',
      'photoViewClicks',
      'postClicks',

      // organic
      'organicComments',
      'organicEffectiveness',
      'organicEffectivenessV2',
      'organicEngagementRate',
      'organicEngagementRateV2',
      'organicEngagements',
      'organicEngagementsV2',
      'organicImpressions',
      'organicReach',
      'organicReactions',
      'organicShares',
      'organicVideoCompleteViews',
      'organicVideoViews',
      'organicViews',

      // promoted
      'paidComments',
      'paidEffectiveness',
      'paidEffectivenessV2',
      'paidEngagementRate',
      'paidEngagementRateV2',
      'paidEngagements',
      'paidImpressions',
      'paidReach',
      'paidReachV2',
      'paidReactions',
      'paidShares',
      'paidVideoCompleteViews',
      'paidVideoViews',
      'paidViews',
      'frequency',
      'thruplays',

      // paid and organic
      'paidAndOrganicComments',
      'paidAndOrganicEffectiveness',
      'paidAndOrganicEffectivenessV2',
      'paidAndOrganicEngagementRate',
      'paidAndOrganicEngagementRateV2',
      'paidAndOrganicEngagements',
      'paidAndOrganicEngagementsV2',
      'paidAndOrganicImpressions',
      'paidAndOrganicLinkClicks',
      'paidAndOrganicOtherClicks',
      'paidAndOrganicPhotoViewClicks',
      'paidAndOrganicPostClicks',
      'paidAndOrganicReach',
      'paidAndOrganicReachV2',
      'paidAndOrganicReactions',
      'paidAndOrganicShares',
      'paidAndOrganicVideoCompleteViews',
      'paidAndOrganicVideoViews',
      'paidAndOrganicViews',
    );
  }
  let insights = omitBy(pick(data.sourceData ?? {}, metricNames), isNil);

  if (isBoosted) {
    insights = {
      ...insights,
      ...extractFacebookPromotedAndOrganicMetrics(data.sourceData),
    };
  }

  let media;
  const carouselUrls = data.sourceData.carouselMediaList;
  if (carouselUrls) {
    const carouselMedia = carouselUrls.map((carouselUrl) => ({
      originalUrl: carouselUrl,
      urls: {
        full: carouselUrl,
        ratio: carouselUrl,
      },
      mediaType: 'IMAGE',
      postSourceId: data.sourceId,
    }));
    media = [mediaDetail, ...carouselMedia];
  }

  return {
    avatarUrl: data.sourceData.pageDetails?.avatar,
    userName: data.sourceData.pageDetails?.name,
    fbPageId: data.sourceData.pageDetails?.fbPageId,
    datePosted: data.sourceData.fbPostCreatedAt,
    sourcePostType: data.sourceData.postType?.toUpperCase(),
    postUrl: data.sourceData.url,
    title: data.sourceData.title,
    message: data.sourceData.message,
    isReel,
    isBoosted,
    // carousel field
    media,
    insights,
    reelInsights,
  };
}

function formatTwitterMediaDetail(data, mediaDetail) {
  // check for carousel

  const {
    engagements,
    engagementsTotal,
    engagementsOrganic,
    engagementsPromoted,
    engagementRate,
    engagementRateTotal,
    engagementRateOrganic,
    engagementRatePromoted,
    follows,
    followsTotal,
    followsOrganic,
    followsPromoted,
    impressions,
    impressionsTotal,
    impressionsOrganic,
    impressionsPromoted,
    likes,
    likesTotal,
    likesOrganic,
    likesPromoted,
    replies,
    repliesTotal,
    repliesOrganic,
    repliesPromoted,
    retweets,
    retweetsTotal,
    retweetsOrganic,
    retweetsPromoted,
    totalPublicEngagements,
    totalRetweets,
    quoteTweets,
    urlClicks,
    urlClicksTotal,
    urlClicksOrganic,
    urlClicksPromoted,
    userProfileClicks,
    userProfileClicksTotal,
    userProfileClicksOrganic,
    userProfileClicksPromoted,
    videoViews,
    videoViewsTotal,
    videoViewsOrganic,
    videoViewsPromoted,
    inReplyToUserId,
    inReplyToStatusId,
    retweetedStatusId,
    quotedStatusId,
  } = data.sourceData;
  const insights = {
    engagements,
    engagementsTotal,
    engagementsOrganic,
    engagementsPromoted,
    engagementRate,
    engagementRateTotal,
    engagementRateOrganic,
    engagementRatePromoted,
    follows,
    followsTotal,
    followsOrganic,
    followsPromoted,
    impressions,
    impressionsTotal,
    impressionsOrganic,
    impressionsPromoted,
    likes,
    likesTotal,
    likesOrganic,
    likesPromoted,
    replies,
    repliesTotal,
    repliesOrganic,
    repliesPromoted,
    retweets,
    retweetsTotal,
    retweetsOrganic,
    retweetsPromoted,
    totalPublicEngagements,
    totalRetweets,
    quoteTweets,
    urlClicks,
    urlClicksTotal,
    urlClicksOrganic,
    urlClicksPromoted,
    userProfileClicks,
    userProfileClicksTotal,
    userProfileClicksOrganic,
    userProfileClicksPromoted,
    videoViews,
    videoViewsTotal,
    videoViewsOrganic,
    videoViewsPromoted,
    inReplyToUserId,
    inReplyToStatusId,
    retweetedStatusId,
    quotedStatusId,
  };

  const carouselUrls = data.sourceData.additionalMediaUrls;
  let media;
  if (carouselUrls) {
    const carouselMedia = carouselUrls.map((carouselUrl) => ({
      originalUrl: carouselUrl,
      urls: {
        full: carouselUrl,
        ratio: carouselUrl,
      },
      mediaType: 'IMAGE',
      postSourceId: data.sourceId,
    }));
    media = [mediaDetail, ...carouselMedia];
  }
  return {
    avatarUrl: data.sourceData.accountDetails?.twitterAvatar,
    datePosted: data.sourceData.tweetCreatedAt,
    postUrl: data.sourceData.permalinkUrl,
    tweetStatus: data.sourceData.text,
    userName: data.sourceData.accountDetails?.handle,
    isPromoted: data.sourceData.isPromoted,
    insights,
    media,
  };
}

function formatTikTokMediaDetail(data) {
  if (data.sourceData) {
    const {
      accountDetails,
      creatorHandleName,
      audienceLocations,
      audienceGenders,
      audienceTypes,
      views,
      totalEngagements,
      likes,
      comments,
      shares,
      emv,
      shareUrl,
      reach,
      totalTimeWatched,
      averageTimeWatched,
      averageCompletionRate,
      fullVideoWatchedRate,
      videoCreatedAt,
      embedUrl,
      caption,
      duration,
      impressionsForYouRate,
      impressionsFollowRate,
      impressionsHashtagRate,
      impressionsSoundRate,
      impressionsPersonalProfileRate,
      impressionsSearchRate,
      engagementRate,
      entertainmentScore,
      favorites,
      newFollowers,
      profileViews: profileViewsCount,
      lastMetricsUpdatedAt,
      videoLink,
      relationship,
      organicViews,
      organicReach,
      organicLikes,
      organicComments,
      organicShares,
      organicEngagements,
      organicEngagementRate,
      organicViewsBasedEngagementRate,
      paidViews,
      paidReach,
      paidLikes,
      paidComments,
      paidShares,
      paidEngagements,
      paidEngagementRate,
      paidViewsBasedEngagementRate,
      viewsBasedEngagementRate,
      advertiserScopedPaidMetrics,
      promotionEndDate,
      paidNewFollowers,
      organicNewFollowers,
      paidProfileViews,
      organicProfileViews,
      mediaType,
    } = data.sourceData;
    const insights = {
      entertainmentScore,
      videoViews: views,
      reach,
      engagementRate,
      viewsBasedEngagementRate,
      retentionRate: averageCompletionRate,
      totalEngagements,
      likes,
      comments,
      shares,
      emv,
      favorites,
      newFollowers,
      totalProfileViews: profileViewsCount,
      videoDuration: duration,
      totalTimeWatched,
      avgTimeWatched: averageTimeWatched,
      completionRate: fullVideoWatchedRate,
      forYouPageViews: impressionsForYouRate,
      followingPageViews: impressionsFollowRate,
      hashtagViews: impressionsHashtagRate,
      soundViews: impressionsSoundRate,
      profileViews: impressionsPersonalProfileRate,
      searchViews: impressionsSearchRate,
      lastMetricsUpdatedAt,
      relationship,
      organicViews,
      organicReach,
      organicLikes,
      organicComments,
      organicShares,
      organicEngagements,
      organicEngagementRate,
      organicViewsBasedEngagementRate,
      paidViews,
      paidReach,
      paidLikes,
      paidComments,
      paidShares,
      paidEngagements,
      paidEngagementRate,
      paidViewsBasedEngagementRate,
      advertiserScopedPaidMetrics,
      paidNewFollowers,
      organicNewFollowers,
      paidProfileViews,
      organicProfileViews,
    };
    return {
      userName: accountDetails?.tiktokDisplayName || creatorHandleName,
      avatarUrl: accountDetails?.tiktokProfileImage,
      datePosted: videoCreatedAt,
      postUrl: videoLink ?? shareUrl,
      embedUrl,
      caption,
      insights,
      tiktokMediaType: mediaType,
      audienceLocations,
      audienceGenders,
      audienceTypes,
      relationship,
      promotionEndDate,
    };
  }
  return {};
}

function formatLinkedinMediaDetail(data) {
  const sourceData = data.sourceData;
  const accountDetails = sourceData.accountDetails;
  const multiImageData = sourceData?.multiImages?.urls ?? [];
  const media = multiImageData.map((imageData) => ({
    mediaType: linkedinMediaTypes.IMAGE,
    sourceType: data.type,
    postSourceId: imageData.imageId,
    urls: {
      full: imageData.url,
    },
  }));
  const {
    clickThroughRate,
    clicks,
    comments,
    engagementRate,
    engagements,
    impressions,
    likes,
    shares,
    uniqueImpressions,
  } = sourceData;
  const insights = {
    clickThroughRate,
    clicks,
    comments,
    engagementRate,
    engagements,
    impressions,
    likes,
    shares,
    uniqueImpressions,
  };
  if (sourceData?.videoAnalytics) {
    insights.videoViews = sourceData.videoAnalytics.videoViews;
    insights.watchTime = sourceData.videoAnalytics.timeWatchedForViews;
  }
  return {
    caption: sourceData.caption,
    userName: accountDetails.name,
    avatarUrl: accountDetails.logo,
    platformHomePageUrl: accountDetails.accountUrl,
    postUrl: sourceData.linkedinLink,
    datePosted: sourceData.sourceCreatedAt,
    insights,
    media,
    mediaType: sourceData.type,
  };
}

function formatThreadsMediaDetail(data) {
  const sourceData = data.sourceData;
  const profileDetails = sourceData.profileDetails;
  const carouselData = sourceData.carouselMediaList || [];

  const media = carouselData.map((mediaData) => ({
    mediaType: mediaData.mediaType,
    sourceType: data.type,
    postSourceId: data.sourceId,
    urls: { full: mediaData.mediaUrl },
  }));

  const insights = {
    views: sourceData.views,
    likes: sourceData.likes,
    replies: sourceData.replies,
    reposts: sourceData.reposts,
    quotes: sourceData.quotes,
    engagements: sourceData.engagements,
    engagementRate: sourceData.engagementRate,
    repostsAndQuotes: sourceData.repostsAndQuotes,
    shares: sourceData.shares,
  };

  return {
    caption: sourceData.text,
    userName: profileDetails.displayName,
    handle: profileDetails.handle,
    avatarUrl: profileDetails.profilePicture,
    postUrl: sourceData.threadsLink,
    datePosted: sourceData.sourcePublishedAt,
    insights,
    media,
    mediaType: sourceData.type,
  };
}

function formatSnapchatMediaDetail(data) {
  const sourceData = data.sourceData;
  const insights = {
    swipeDowns: sourceData.swipeDowns,
    swipeUps: sourceData.swipeUps,
    title: sourceData.title,
    uniqueSessions: sourceData.uniqueSessions,
    viewTimeSeconds: sourceData.viewTimeSeconds,
    viewers: sourceData.viewers,
    views: sourceData.views,
    duration: sourceData.duration,
    engagementRate: sourceData.engagementRate,
    engagements: sourceData.engagements,
    favorites: sourceData.favorites,
    interactions: sourceData.interactions,
    replies: sourceData.replies,
    screenshots: sourceData.screenshots,
    shares: sourceData.shares,
    avgViewTimeSeconds: sourceData.avgViewTimeSeconds,
    completes: sourceData.completes,
  };

  return {
    postTitle: sourceData.title,
    caption: sourceData.caption,
    sourceProfileId: sourceData.sourceProfileId,
    datePosted: sourceData.sourceCreatedAt,
    insights,
    mediaType: sourceData.assetType,
  };
}

export function formatChannelMediaDetail(data, brand, mediaDetail, adId) {
  switch (mediaDetail.postType) {
    case constants.INSTAGRAM:
      return formatInstagramMediaDetail(data);
    case constants.PINTEREST:
      return formatPinterestMediaDetail(data, mediaDetail);
    case constants.UPLOAD:
      return formatUploadMediaDetail(data);
    case constants.AI_GENERATION:
      return formatAiGenerationMediaDetail(data);
    case constants.INSTAGRAM_STORY:
      return formatInstagramStoryMediaDetail(data);
    case constants.FACEBOOK:
      return formatFacebookMediaDetail(data, brand, mediaDetail, adId);
    case constants.TWITTER:
      return formatTwitterMediaDetail(data, mediaDetail);
    case constants.TIKTOK:
      return formatTikTokMediaDetail(data);
    case constants.YOUTUBE:
      return formatYouTubeMediaDetail(
        mediaDetail.sourceType === enumTypes.YOUTUBE_OWNED,
        data.sourceData,
      );
    case constants.LINKEDIN:
      return formatLinkedinMediaDetail(data);
    case constants.THREADS:
      return formatThreadsMediaDetail(data);
    case constants.SNAPCHAT:
      return formatSnapchatMediaDetail(data);
    default:
      return {};
  }
}

export function formatMediaDetailData(data, brand, adId) {
  // shared fields for all types of media
  // TODO(pmdarrow): This manual post-processing of the media object makes it hard to guess what
  // server field values are on the client. At most we should be camelCasing the snake_case fields,
  // and leave the data as-is. If the data coming from the server isn't the way we want it on the
  // client, we should work with the backend devs to make it correct / easier to use.
  const {
    tags,
    systemTags,
    mediaType,
    urls,
    meta,
    predictions,
    likeshopClicks,
    tiktokLikeshopClicks,
    imageSizes,
    originalHeight,
    originalWidth,
    duration,
    videoSizes,
    videoConversionWarnings,
    galleries,
    campaigns,
    captionSentiment,
    commentSentiment,
    triggeredContentAutomations,
    aiAlternatives,
  } = data;
  const mediaDetail = {
    _original: data,
    id: data.mediaId,
    brandId: brand.id,
    mediaGroupId: data.mediaGroup,
    tags,
    systemTags,
    // TODO: take care of it when we have video type media detail specs
    mediaType,
    media_type: mediaType,
    url: urls?.original,
    urls,
    meta,
    predictions,
    // current enum for sources: PINTEREST, INSTAGRAM
    postType: data.source,
    postSourceId: data.sourceId,
    sourceType: data.type,
    likeshopClicks,
    tiktokLikeshopClicks,
    imageUrl: urls?.full ? urls.full : urls?.original,
    imageSizes,
    originalHeight: parseInt(originalHeight, 10),
    originalWidth: parseInt(originalWidth, 10),
    videoUrl: urls?.full,
    duration,
    videoConversionWarnings,
    videoSizes,
    originalUrl: urls?.original,
    galleries,
    campaigns,
    captionSentiment,
    commentSentiment,
    triggeredContentAutomations,
    aiAlternatives,
  };
  // workaround for issue when source data is not indexed in ES
  const sourceDataToCheck = data.sourceData || {};
  const sourceDataHasError =
    Object.keys(sourceDataToCheck).length === 1 && 'description' in sourceDataToCheck;
  if (!data?.sourceData || sourceDataHasError) {
    return mediaDetail;
  }
  return { ...mediaDetail, ...formatChannelMediaDetail(data, brand, mediaDetail, adId) };
}

async function getFacebookAdMediaData(mediaIds, brandId) {
  try {
    const promiseList = mediaIds.map((id) => {
      const adMediaUrl = `/brands/${brandId}/media/${id}`;
      return axios.get(adMediaUrl);
    });
    const dataList = await Promise.all(promiseList);
    return dataList.map((d) => d.data);
  } catch {
    // If failed, treat it as a single media.
    return null;
  }
}

function getURL(source, brandId, mediaId, start, end) {
  if (source) {
    const queryString = stringifyQuery({ source_type: source });
    return `/brands/${brandId}/media/source/${mediaId}?${queryString}`;
  }
  if (start && end) {
    const queryString = stringifyQuery({
      insights_start_date: dayjs(start).format('YYYY-MM-DD'),
      insights_end_date: dayjs(end).format('YYYY-MM-DD'),
    });
    return `/brands/${brandId}/media/${mediaId}?${queryString}&allow_index=true`;
  }
  return `/brands/${brandId}/media/${mediaId}?allow_index=true`;
}

/**
 * Do a summary call to library to check if the requested media exists. If it does
 * not exist, make a call to either tiktok-backend or instagram-backend to import
 * it on the fly.
 *
 * @param source The platform the post belongs to -- either TIKTOK or INSTAGRAM.
 * @param url The URL to the post in library.
 * @param patchJson Extra parameters sent to the Instagram API.
 * @param brandId The brand ID.
 * @param mediaId The media ID.
 * @returns {Promise<void>}
 */
async function indexPostIfMissingFromLibrary({ source, url, patchJson, brandId, mediaId }) {
  const importMediaBySource = {
    [mediaSourceType.INSTAGRAM]: async () => {
      await instagramAxios.patch(`/brands/${brandId}/instagram/posts/${mediaId}`, patchJson);
    },
    [mediaSourceType.TIKTOK]: async () => {
      await tiktokApi.createMediaForVideo({
        brandId,
        videoId: mediaId,
        handleName: patchJson?.tiktok_name,
      });
    },
    [mediaSourceType.YOUTUBE]: async () => {
      await youtubeApi.createMediaForVideo({
        brandId,
        sourceVideoId: mediaId,
      });
    },
  };
  if (!(source in importMediaBySource)) return;

  try {
    const summaryUrl = url.includes('?') ? `${url}&summary=true` : `${url}?summary=true`;
    await axios.get(summaryUrl);
  } catch {
    await importMediaBySource[source]();
  }
}

function replaceMimeType(result, originalUrl) {
  const extension = originalUrl.split('.').pop();
  let mimeType;
  switch (extension) {
    case 'jpg':
    case 'jpeg':
      mimeType = 'image/jpeg';
      break;
    case 'png':
      mimeType = 'image/png';
      break;
    case 'gif':
      mimeType = 'image/gif';
      break;
    default:
      mimeType = 'image/jpeg';
  }
  return result.replace('data:application/octet-stream;', `data:${mimeType};`);
}

async function imageToBase64(originalUrl) {
  const dashDomains = [
    'images.dhdev.co',
    'images.dashhudson.com',
    'images.dashsocial.com',
    'cdn.dhdev.co',
    'cdn.dashsocial.com',
    'cdn.dashhudson.com',
  ];

  let url = originalUrl;
  const useProxy = dashDomains.some((domain) => url.includes(domain));

  if (useProxy) {
    url = `https://dashboard.dashhudson.com/export_proxy?url=${originalUrl}`;
  }

  const axiosInstance = axiosForExternalCall.create({
    headers: {
      Origin: url,
    },
  });

  const response = await axiosInstance.get(url, {
    responseType: 'blob',
    withCredentials: false,
  });
  const blob = response.data;
  const reader = new FileReader();
  return new Promise((resolve, reject) => {
    reader.onloadend = () => {
      if (reader.readyState === FileReader.DONE) {
        let result = reader.result;
        if (result && useProxy) {
          result = replaceMimeType(result, url);
        }
        resolve(result);
      } else {
        reject(new Error('Error reading image data.'));
      }
    };
    reader.onerror = () => {
      reject(new Error('Error reading image data.'));
    };
    reader.readAsDataURL(blob);
  });
}

async function handleCorruptedCarouselChildMedia(formattedMediaDetail) {
  if (
    formattedMediaDetail?.postType === mediaSourceType.INSTAGRAM &&
    formattedMediaDetail?.media?.length > 1
  ) {
    try {
      await externalAxios.head(formattedMediaDetail.media[0].urls.full);
    } catch (e) {
      if (e.response?.status === 403) {
        formattedMediaDetail.fallbackCarouselMedia = [
          {
            mediaType: formattedMediaDetail.mediaType,
            postSourceId: formattedMediaDetail.postSourceId,
            urls: {
              full: formattedMediaDetail.urls.full,
              ratio: formattedMediaDetail.urls.ratio,
            },
          },
        ];
      }
    }
  }
}

export const useMediaDetailStore = defineStore(
  'mediaDetail',
  () => {
    const pending = ref({
      multipleMediaTagsStatus: false,
      updateMediaSentimentStatus: false,
      updateMediaNotesStatus: false,
      addMediaItemStatus: false,
      deleteMediaItemStatus: false,
      bulkDeleteMediaStatus: false,
    });

    const error = ref({});
    const mediaTags = ref(null);
    const imageDataDetails = ref(null);
    const mediaDetail = ref(null);
    const mediaDetailRequestStatus = ref(null);
    const customMetricsDetail = ref(null);
    const customMetricsDetailRequestStatus = ref(null);
    const customMetricsNotFound = ref(false);
    const showMediaPopup = ref(null);
    const imageDataMediaType = ref(null);
    const saveToFBAssetsResult = ref(null);

    async function addMediaTag({ brandId, mediaId, name }) {
      const { data } = await LibraryAPI.addMediaTag({ brandId, mediaId, name });
      mediaTags.value = data;
    }

    async function addMultipleMediaTags({ brandId, mediaIds, tags }) {
      pending.value.multipleMediaTagsStatus = true;
      try {
        return await LibraryAPI.addMultipleMediaTags({ brandId, mediaIds, tags });
      } finally {
        pending.value.multipleMediaTagsStatus = false;
      }
    }

    async function removeMediaTag({ brandId, mediaId, tagId }) {
      const { data } = await LibraryAPI.removeMediaTag({ brandId, mediaId, tagId });
      mediaTags.value = data;
    }

    async function updateMediaNotes({ brandId, mediaId, meta }) {
      pending.value.updateMediaNotesStatus = true;
      try {
        return await LibraryAPI.updateMediaMeta({ brandId, mediaId, meta });
      } finally {
        pending.value.updateMediaNotesStatus = false;
      }
    }

    async function updateMediaSentiment({ brandId, mediaId, meta }) {
      pending.value.updateMediaSentimentStatus = true;
      try {
        const resp = await LibraryAPI.updateMediaMeta({ brandId, mediaId, meta });
        this.setMediaDetail({
          mediaItem: { ...mediaDetail.value, captionSentiment: resp.data.captionSentiment },
        });
      } finally {
        pending.value.updateMediaSentimentStatus = false;
      }
    }

    async function notifyAddMediaItem() {
      pending.value.addMediaItemStatus = true;
      await nextTick(); // Flush watchers
      pending.value.addMediaItemStatus = false;
    }

    async function deleteMediaItem({ brandId, mediaId }) {
      pending.value.deleteMediaItemStatus = true;
      try {
        return await LibraryAPI.deleteMediaItem({ brandId, mediaId });
      } finally {
        pending.value.deleteMediaItemStatus = false;
      }
    }

    async function bulkDeleteMedia({ brandIds, mediaIds }) {
      pending.value.bulkDeleteMediaStatus = true;
      try {
        return await LibraryAPI.bulkDeleteMedia({ brandIds, mediaIds });
      } finally {
        pending.value.bulkDeleteMediaStatus = false;
      }
    }

    async function getImageDataByMediaId({ mediaId }) {
      const { data } = await LibraryAPI.getImageDataByMediaId({ mediaId });
      imageDataDetails.value = data;
    }
    async function getImageDataByUrl({ url }) {
      const data = await imageToBase64(url);
      imageDataDetails.value = {
        imageData: data,
      };
    }

    async function getMediaDetail({
      mediaId,
      source,
      adId,
      insightsStart,
      insightsEnd,
      patchJson,
      brandId,
    }) {
      // this is where we fetch all the mediaItem details- we format the resulting store variable based on what channel it's for
      const authStore = useAuthStore();
      mediaDetail.value = null;
      mediaDetailRequestStatus.value = 'pending';
      const mediaBrand = authStore.identityBrandsById[brandId];
      const brand = mediaBrand || authStore.currentBrand;
      const brandMediaURL = getURL(source, brand.id, mediaId, insightsStart, insightsEnd);
      try {
        await indexPostIfMissingFromLibrary({
          source: source?.toUpperCase(),
          url: brandMediaURL,
          patchJson: patchJson || {},
          brandId: brand.id,
          mediaId,
        });
        const { data } = await axios.get(brandMediaURL);
        const formattedMediaDetail = formatMediaDetailData(data, brand, adId);
        if (
          formattedMediaDetail.sourceType === mediaSourceType.FACEBOOK_ADS &&
          formattedMediaDetail.mediaIds.length > 1
        ) {
          formattedMediaDetail.media =
            (await getFacebookAdMediaData(formattedMediaDetail.mediaIds, brand.id)) ??
            formattedMediaDetail.media;
        }
        await handleCorruptedCarouselChildMedia(formattedMediaDetail);
        this.setMediaDetail({ mediaItem: formattedMediaDetail });
      } catch (err) {
        this.setMediaDetail({ mediaItem: null });
        if (err?.response?.status === 404) {
          this.mediaNotFound = true;
        } else {
          this.mediaNotFound = false;
          throw err;
        }
      }
    }

    async function getCustomMetricsForMedia(mediaId, brandId) {
      customMetricsDetailRequestStatus.value = 'pending';
      const customMetricsMediaURL = `/brands/${brandId}/media/${mediaId}/custom_metrics`;
      try {
        const { data } = await axios.get(customMetricsMediaURL);
        this.setCustomMetricsDetail({
          customMetricsItem: data.customMetrics.sort((a, b) => {
            const nameA = a.name.toUpperCase();
            const nameB = b.name.toUpperCase();
            if (nameA < nameB) {
              return -1;
            }
            if (nameA > nameB) {
              return 1;
            }
            return 0;
          }),
        });
      } catch (err) {
        this.setCustomMetricsDetail({ customMetricsItem: null });
        if (err?.response?.status === 404) {
          customMetricsNotFound.value = true;
        }
      }
    }

    function setMediaDetail({ mediaItem }) {
      mediaDetail.value = mediaItem;
      mediaDetailRequestStatus.value = null;
    }

    function setCustomMetricsDetail({ customMetricsItem }) {
      customMetricsDetail.value = customMetricsItem;
      customMetricsDetailRequestStatus.value = null;
    }

    function clearMediaDetail() {
      mediaDetail.value = null;
      mediaDetailRequestStatus.value = null;
    }

    function clearCustomMetricsDetails() {
      customMetricsDetail.value = null;
      customMetricsDetailRequestStatus.value = null;
      customMetricsNotFound.value = false;
    }

    function openMediaPopup() {
      showMediaPopup.value = true;
    }

    function closeMediaPopup() {
      showMediaPopup.value = false;
    }

    function updateIsRIQ({ value }) {
      if (mediaDetail.value && value) {
        mediaDetail.value.isRiq = value;
      }
    }

    function setMediaDetailInsights({ insights }) {
      if (mediaDetail.value && insights) {
        mediaDetail.value.insights = insights;
      }
    }

    function setMediaDetailContentRightsRequested({ requested }) {
      if (mediaDetail.value && requested) {
        mediaDetail.value.contentRightsRequested = requested;
      }
    }

    function setImageDataMediaType({ mediaType }) {
      imageDataMediaType.value = mediaType;
    }

    return {
      pending,
      error,
      mediaTags,
      imageDataDetails,
      mediaDetail,
      mediaDetailRequestStatus,
      customMetricsDetail,
      customMetricsDetailRequestStatus,
      customMetricsNotFound,
      imageDataMediaType,
      addMediaTag,
      addMultipleMediaTags,
      removeMediaTag,
      updateMediaNotes,
      updateMediaSentiment,
      notifyAddMediaItem,
      deleteMediaItem,
      bulkDeleteMedia,
      getImageDataByMediaId,
      getImageDataByUrl,
      setMediaDetail,
      setCustomMetricsDetail,
      showMediaPopup,
      saveToFBAssetsResult,
      clearMediaDetail,
      clearCustomMetricsDetails,
      openMediaPopup,
      closeMediaPopup,
      updateIsRIQ,
      setMediaDetailInsights,
      setMediaDetailContentRightsRequested,
      setImageDataMediaType,
      getMediaDetail,
      getCustomMetricsForMedia,
      imageToBase64,
    };
  },
  {
    resetOnBrandChange: true,
  },
);
